/**
 * Admin backup surface exposed under `client.storage.backup.*`.
 *
 * Routes live at `/admin/backups*` (admin / elevated key). Archives land in the
 * server-configured S3 or R2 profile (`ATHENA_BACKUP_S3_*` including provider + account_id).
 */

import type { AthenaGatewayClient } from '../gateway/client.ts'
import type {
  AthenaGatewayEndpointPath,
  AthenaGatewayMethod,
} from '../gateway/types.ts'
import { buildAthenaGatewayUrl, normalizeAthenaGatewayBaseUrl } from '../gateway/url.ts'
import type { AthenaStorageCallOptions, AthenaStorageClientConfig } from './module.ts'

/** Avoid circular import with module.ts — shape matches AthenaStorageError enough for callers. */
function backupError(
  message: string,
  status: number,
  endpoint: string,
  method: string,
  cause?: unknown,
  raw?: unknown,
): never {
  const error = new Error(message) as Error & {
    name: string
    code: string
    status: number
    endpoint: string
    method: string
    cause?: unknown
    raw?: unknown
  }
  error.name = 'AthenaStorageError'
  error.code = status === 0 ? 'NETWORK_ERROR' : 'HTTP_ERROR'
  error.status = status
  error.endpoint = endpoint
  error.method = method
  error.cause = cause
  error.raw = raw
  throw error
}

export type BackupRecoveryStrategy = 'none' | 'repair_missing_role_oids'

export interface StorageBackupRecord {
  job_id: number
  s3_bucket: string
  s3_key: string
  /** Alias of s3_key for convenience */
  key: string
  client_name: string
  label?: string | null
  size_bytes: number
  execution_daemon_id?: string | null
  completed_at: string
}

export interface StorageBackupListPage {
  backups: StorageBackupRecord[]
  next_cursor: string | null
}

export interface StorageBackupListQuery {
  client_name?: string
  cursor?: string | null
  limit?: number
}

export interface StorageBackupCreateRequest {
  client_name: string
  label?: string
  pg_uri?: string
  recovery_strategy?: BackupRecoveryStrategy
}

export interface StorageBackupQueuedJob {
  job_id: number
  client_name: string
  status: string
  key?: string
}

export interface StorageBackupRestoreRequest {
  client_name: string
  pg_uri?: string
}

export interface StorageBackupJob {
  id: number
  job_type: string
  client_name?: string | null
  status: string
  progress_pct?: number | null
  progress_stage?: string | null
  s3_bucket?: string | null
  s3_key?: string | null
  label?: string | null
  error_message?: string | null
  created_at?: string
  updated_at?: string
  completed_at?: string | null
  [key: string]: unknown
}

export interface StorageBackupSchedule {
  id: number
  client_name: string
  frequency?: string
  enabled?: boolean
  label?: string | null
  [key: string]: unknown
}

export interface StorageBackupScheduleCreateRequest {
  client_name: string
  frequency: string
  time_of_day?: string
  day_of_week?: number
  day_of_month?: number
  label?: string
  execution_daemon_id?: string
  enabled?: boolean
  [key: string]: unknown
}

export interface AthenaStorageBackupNamespace {
  /** `GET /admin/backups` */
  list(
    query?: StorageBackupListQuery,
    options?: AthenaStorageCallOptions,
  ): Promise<StorageBackupListPage>
  /** `POST /admin/backups` — queue a backup job */
  create(
    input: StorageBackupCreateRequest,
    options?: AthenaStorageCallOptions,
  ): Promise<StorageBackupQueuedJob>
  /** `POST /admin/backups/{key}/restore` */
  restore(
    key: string,
    input: StorageBackupRestoreRequest,
    options?: AthenaStorageCallOptions,
  ): Promise<StorageBackupQueuedJob>
  /** `DELETE /admin/backups/{key}` */
  delete(key: string, options?: AthenaStorageCallOptions): Promise<void>
  /** Build a browser download URL for `GET /admin/backups/{key}/download` */
  downloadUrl(key: string, options?: { apiKey?: string }): string
  jobs: {
    list(
      query?: { limit?: number; status?: string; client_name?: string },
      options?: AthenaStorageCallOptions,
    ): Promise<StorageBackupJob[]>
    get(jobId: number | string, options?: AthenaStorageCallOptions): Promise<StorageBackupJob>
    cancel(jobId: number | string, options?: AthenaStorageCallOptions): Promise<Record<string, unknown>>
    delete(jobId: number | string, options?: AthenaStorageCallOptions): Promise<void>
    /** Presigned/console open link for the job archive object (S3 or R2) */
    openObjectUrl(jobId: number | string, options?: AthenaStorageCallOptions): Promise<string>
  }
  schedules: {
    list(options?: AthenaStorageCallOptions): Promise<StorageBackupSchedule[]>
    create(
      input: StorageBackupScheduleCreateRequest,
      options?: AthenaStorageCallOptions,
    ): Promise<StorageBackupSchedule>
    update(
      id: number | string,
      input: Partial<StorageBackupScheduleCreateRequest>,
      options?: AthenaStorageCallOptions,
    ): Promise<StorageBackupSchedule>
    delete(id: number | string, options?: AthenaStorageCallOptions): Promise<void>
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function asPath(path: string): AthenaGatewayEndpointPath {
  return path as AthenaGatewayEndpointPath
}

function appendQuery(path: string, query?: object): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const text = params.toString()
  return text ? `${path}?${text}` : path
}

function unwrapAthenaData<T>(payload: unknown): T {
  if (isRecord(payload) && 'data' in payload) {
    return payload.data as T
  }
  return payload as T
}

function requireKey(key: string): string {
  const trimmed = key.trim()
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
    backupError('Backup object key is required', 400, '/admin/backups/{key}', 'DELETE')
  }
  return trimmed
}

function resolveMessage(payload: unknown, fallback: string): string {
  if (isRecord(payload)) {
    const message = payload.message ?? payload.error ?? payload.details
    if (typeof message === 'string' && message.trim()) return message.trim()
  }
  if (typeof payload === 'string' && payload.trim()) return payload.trim()
  return fallback
}

async function callBackupEndpoint<T>(
  gateway: AthenaGatewayClient,
  path: string,
  method: AthenaGatewayMethod,
  payload: unknown | undefined,
  options: AthenaStorageCallOptions | undefined,
  runtime: AthenaStorageClientConfig | undefined,
): Promise<T> {
  const resolvedOptions = await gateway.resolveCallOptions(options)
  const endpoint = asPath(path)
  const baseUrl = resolvedOptions?.baseUrl
    ? normalizeAthenaGatewayBaseUrl(resolvedOptions.baseUrl)
    : gateway.baseUrl
  const url = buildAthenaGatewayUrl(baseUrl, endpoint)
  void runtime
  const headers = gateway.buildHeaders(resolvedOptions)
  const init: RequestInit = {
    method,
    headers,
    signal: resolvedOptions?.signal,
  }
  if (payload !== undefined && method !== 'GET') {
    init.body = JSON.stringify(payload)
  }

  let response: Response
  try {
    response = await fetch(url, init)
  } catch (error) {
    backupError(
      error instanceof Error ? error.message : 'Backup network error',
      0,
      path,
      method,
      error,
    )
  }

  const rawText = await response.text()
  let parsed: unknown = null
  if (rawText) {
    try {
      parsed = JSON.parse(rawText)
    } catch {
      parsed = rawText
    }
  }

  if (!response.ok) {
    backupError(
      resolveMessage(parsed, `Backup ${method} ${path} failed (${response.status})`),
      response.status,
      path,
      method,
      undefined,
      parsed,
    )
  }

  return unwrapAthenaData<T>(parsed)
}

/**
 * Create the `storage.backup` namespace bound to an Athena gateway client.
 */
export function createStorageBackupModule(
  gateway: AthenaGatewayClient,
  runtimeOptions?: AthenaStorageClientConfig,
): AthenaStorageBackupNamespace {
  const resolvedBaseUrl = () => String(gateway.baseUrl ?? '').replace(/\/+$/, '')

  return {
    async list(query, options) {
      const path = appendQuery('/admin/backups', {
        limit: query?.limit ?? 10,
        client_name: query?.client_name,
        cursor: query?.cursor ?? undefined,
      })
      const data = await callBackupEndpoint<{
        backups?: Array<Omit<StorageBackupRecord, 'key'> & { key?: string }>
        next_cursor?: string | null
      }>(gateway, path, 'GET', undefined, options, runtimeOptions)

      const backups = (data?.backups ?? []).map((row) => ({
        ...row,
        key: row.key ?? row.s3_key,
      }))
      return {
        backups,
        next_cursor: data?.next_cursor ?? null,
      }
    },

    async create(input, options) {
      const data = await callBackupEndpoint<{
        job_id?: number
        client_name?: string
        status?: string
      }>(gateway, '/admin/backups', 'POST', input, options, runtimeOptions)
      return {
        job_id: Number(data?.job_id ?? 0),
        client_name: String(data?.client_name ?? input.client_name),
        status: String(data?.status ?? 'pending'),
      }
    },

    async restore(key, input, options) {
      const backupKey = requireKey(key)
      const path = `/admin/backups/${encodeURIComponent(backupKey)}/restore`
      const data = await callBackupEndpoint<{
        job_id?: number
        key?: string
        client_name?: string
        status?: string
      }>(gateway, path, 'POST', input, options, runtimeOptions)
      return {
        job_id: Number(data?.job_id ?? 0),
        client_name: String(data?.client_name ?? input.client_name),
        status: String(data?.status ?? 'pending'),
        key: String(data?.key ?? backupKey),
      }
    },

    async delete(key, options) {
      const backupKey = requireKey(key)
      await callBackupEndpoint(
        gateway,
        `/admin/backups/${encodeURIComponent(backupKey)}`,
        'DELETE',
        undefined,
        options,
        runtimeOptions,
      )
    },

    downloadUrl(key, options) {
      const backupKey = requireKey(key)
      const base = `${resolvedBaseUrl()}/admin/backups/${encodeURIComponent(backupKey)}/download`
      return options?.apiKey
        ? `${base}?api_key=${encodeURIComponent(options.apiKey)}`
        : base
    },

    jobs: {
      async list(query, options) {
        const path = appendQuery('/admin/backups/jobs', query ?? {})
        const data = await callBackupEndpoint<
          StorageBackupJob[] | { jobs?: StorageBackupJob[] }
        >(gateway, path, 'GET', undefined, options, runtimeOptions)
        if (Array.isArray(data)) return data
        if (isRecord(data) && Array.isArray(data.jobs)) return data.jobs
        return []
      },
      async get(jobId, options) {
        return callBackupEndpoint(
          gateway,
          `/admin/backups/jobs/${encodeURIComponent(String(jobId))}`,
          'GET',
          undefined,
          options,
          runtimeOptions,
        )
      },
      async cancel(jobId, options) {
        return callBackupEndpoint(
          gateway,
          `/admin/backups/jobs/${encodeURIComponent(String(jobId))}/cancel`,
          'POST',
          {},
          options,
          runtimeOptions,
        )
      },
      async delete(jobId, options) {
        await callBackupEndpoint(
          gateway,
          `/admin/backups/jobs/${encodeURIComponent(String(jobId))}`,
          'DELETE',
          undefined,
          options,
          runtimeOptions,
        )
      },
      async openObjectUrl(jobId, options) {
        const data = await callBackupEndpoint<{ url?: string }>(
          gateway,
          `/admin/backups/jobs/${encodeURIComponent(String(jobId))}/s3-open`,
          'GET',
          undefined,
          options,
          runtimeOptions,
        )
        return String(data?.url ?? '')
      },
    },

    schedules: {
      async list(options) {
        const data = await callBackupEndpoint<
          StorageBackupSchedule[] | { schedules?: StorageBackupSchedule[] }
        >(gateway, '/admin/backups/schedules', 'GET', undefined, options, runtimeOptions)
        if (Array.isArray(data)) return data
        if (isRecord(data) && Array.isArray(data.schedules)) return data.schedules
        return []
      },
      async create(input, options) {
        return callBackupEndpoint(
          gateway,
          '/admin/backups/schedules',
          'POST',
          input,
          options,
          runtimeOptions,
        )
      },
      async update(id, input, options) {
        return callBackupEndpoint(
          gateway,
          `/admin/backups/schedules/${encodeURIComponent(String(id))}`,
          'PATCH',
          input,
          options,
          runtimeOptions,
        )
      },
      async delete(id, options) {
        await callBackupEndpoint(
          gateway,
          `/admin/backups/schedules/${encodeURIComponent(String(id))}`,
          'DELETE',
          undefined,
          options,
          runtimeOptions,
        )
      },
    },
  }
}
